import { League, Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

interface SettingsTabProps {
  league: League;
  tournaments: Tournament[];
  isOwner: boolean;
  onUpdateLeague: (updates: Partial<League>) => void;
  onCompleteLeague: () => void;
  onDeleteLeague: () => void;
  updatingLeague: boolean;
  onUploadLogo: () => void;
  uploadingLogo: boolean;
  colors: any;
}

export const SettingsTab = ({
  league,
  tournaments,
  isOwner,
  onUpdateLeague,
  onCompleteLeague,
  onDeleteLeague,
  updatingLeague,
  onUploadLogo,
  uploadingLogo,
  colors,
}: SettingsTabProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(league.name);
  const [description, setDescription] = useState(league.description || '');

  const allCompleted = tournaments.length > 0 && tournaments.every((t) => t.status === 'completed');

  const handleSave = () => {
    onUpdateLeague({
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  const handleTogglePublic = () => {
    onUpdateLeague({ isPublic: !league.isPublic });
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      {isOwner && (
        <>
          <View style={styles.section}>
            <Text style={[styles.label, { color: colors.text }]}>{t('leagues.logo')}</Text>
            <View style={styles.logoRow}>
              <View style={[styles.logoCircle, { backgroundColor: colors.card, borderColor: colors.border }]}>
                {league.logoUrl ? (
                  <Image source={{ uri: league.logoUrl }} style={styles.logoImage} />
                ) : (
                  <MaterialCommunityIcons name="trophy-outline" size={24} color={colors.textTertiary} />
                )}
              </View>
              <TouchableOpacity
                style={[styles.changeLogoButton, { backgroundColor: colors.card, borderColor: colors.border }]}
                onPress={onUploadLogo}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <>
                    <Ionicons name="camera-outline" size={16} color={colors.primary} />
                    <Text style={[styles.changeLogoText, { color: colors.primary }]}>
                      {league.logoUrl ? t('leagues.changeLogo') : t('leagues.uploadLogo')}
                    </Text>
                  </>
                )}
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={[styles.label, { color: colors.text }]}>{t('leagues.leagueName')}</Text>
            <TextInput
              style={[styles.input, { backgroundColor: colors.card, color: colors.text, borderColor: colors.border }]}
              value={name}
              onChangeText={setName}
            />
          </View>

          <View style={styles.section}>
            <Text style={[styles.label, { color: colors.text }]}>{t('leagues.description')}</Text>
            <TextInput
              style={[styles.input, styles.textArea, { backgroundColor: colors.card, color: colors.text, borderColor: colors.border }]}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={3}
            />
          </View>
        </>
      )}


      <View style={[styles.visibilityRow, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.visibilityInfo}>
          <Text style={[styles.visibilityTitle, { color: colors.text }]}>
            {league.isPublic ? t('leagues.public') : t('leagues.private')}
          </Text>
          <Text style={[styles.visibilityDesc, { color: colors.textSecondary }]}>
            {league.isPublic ? t('leagues.publicDesc') : t('leagues.privateDesc')}
          </Text>
          {isOwner && !league.isPublic && (
            <Text style={[styles.visibilityWarning, { color: colors.textTertiary }]}>
              {t('leagues.publicTournamentsWarning')}
            </Text>
          )}
        </View>
        {isOwner && (
          <Switch
            value={league.isPublic}
            onValueChange={handleTogglePublic}
            trackColor={{ false: colors.border, true: colors.primary }}
            thumbColor={colors.primaryText}
            disabled={updatingLeague}
          />
        )}
      </View>

      {isOwner && (
        <>
          <TouchableOpacity
            style={[styles.saveButton, { backgroundColor: colors.primary }, updatingLeague && styles.disabled]}
            onPress={handleSave}
            disabled={updatingLeague}
          >
            {updatingLeague ? (
              <ActivityIndicator color={colors.primaryText} />
            ) : (
              <Text style={[styles.saveButtonText, { color: colors.primaryText }]}>{t('common.save')}</Text>
            )}
          </TouchableOpacity>

          {league.status === 'active' && (
            <TouchableOpacity
              style={[styles.completeButton, { backgroundColor: allCompleted ? colors.badgeCompleted : colors.card, borderColor: colors.border }]}
              onPress={onCompleteLeague}
              disabled={!allCompleted}
            >
              <Ionicons name="checkmark-circle" size={20} color={allCompleted ? colors.text : colors.textTertiary} />
              <Text style={[styles.completeButtonText, { color: allCompleted ? colors.text : colors.textTertiary }]}>
                {t('leagues.completeLeague')}
              </Text>
            </TouchableOpacity>
          )}

          {!allCompleted && league.status === 'active' && (
            <Text style={[styles.warningText, { color: colors.textTertiary }]}>
              {t('leagues.notAllTournamentsCompleted')}
            </Text>
          )}

          <TouchableOpacity
            style={[styles.deleteButton, { borderColor: colors.error }]}
            onPress={onDeleteLeague}
          >
            <Ionicons name="trash-outline" size={20} color={colors.error} />
            <Text style={[styles.deleteButtonText, { color: colors.error }]}>{t('leagues.deleteLeague')}</Text>
          </TouchableOpacity>
        </>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: { padding: 20, gap: 20, paddingBottom: 40 },
  section: { gap: 8 },
  label: { fontSize: 16, fontWeight: '600' },
  input: { padding: 14, borderRadius: 10, fontSize: 16, borderWidth: 1 },
  textArea: { minHeight: 80, textAlignVertical: 'top' },
  logoRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  logoCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  logoImage: { width: 56, height: 56, borderRadius: 28 },
  changeLogoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  changeLogoText: { fontSize: 14, fontWeight: '600' },
  pointSystemDisplay: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  pointChip: { alignItems: 'center', paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1 },
  pointChipPosition: { fontSize: 11, fontWeight: '600' },
  pointChipValue: { fontSize: 15, fontWeight: '700' },
  visibilityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 10,
    borderWidth: 1,
    gap: 12,
  },
  visibilityInfo: { flex: 1, gap: 2 },
  visibilityTitle: { fontSize: 15, fontWeight: '600' },
  visibilityDesc: { fontSize: 12 },
  visibilityWarning: { fontSize: 11, marginTop: 4 },
  saveButton: { padding: 16, borderRadius: 10, alignItems: 'center' },
  saveButtonText: { fontSize: 16, fontWeight: '600' },
  completeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
  },
  completeButtonText: { fontSize: 16, fontWeight: '600' },
  warningText: { fontSize: 13, textAlign: 'center' },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 10,
    borderWidth: 1,
    gap: 8,
    marginTop: 10,
  },
  deleteButtonText: { fontSize: 16, fontWeight: '600' },
  disabled: { opacity: 0.6 },
});
